import { MutableRefObject, useCallback, useMemo, useEffect } from 'react';
import { isEmpty, isEqual, isObject, isArray, toString, map } from 'lodash';
import {
    GridColDef,
    GridExcelExportOptions,
    GridRenderCellParams,
    GridRowId,
    GridSortItem,
    GridValidRowModel,
    GridFileExportOptions,
} from '@mui/x-data-grid-premium';
import { GridApiPremium } from '@mui/x-data-grid-premium/models/gridApiPremium';
import {
    DataGridSearchFilter,
    DataGridState,
    DataGridStateType,
    DataGridStateView,
    GRID_NAME,
    LoopbackFilter,
    SearchCriteria,
} from 'common/types/datagrid';
import * as logger from 'common/utils/Logger';
import { RESULTS_PER_PAGE } from 'common/utils/constants';
import { generateRandomUUID } from 'common/utils/Utils';
import {
    loadGridState,
    setGridViewActiveId,
    initializeGridState,
    setActivePaginationPage,
    setActiveQuery,
    setAdvancedFilterCount,
    setColumnOrdering,
} from 'common/stores/redux/DataGrid/DataGrid';
import { useDispatch } from 'react-redux';
import { AdvancedFiltersState } from 'common/context/DataGridAdvancedFilterContext';
import { GridExceljsProcessInput } from '@mui/x-data-grid-premium/hooks/features/export/gridExcelExportInterface';

interface CustomColumnType {
    headerName: string;
    field: string;
    valueGetter: (value: any, row: any, column: any, api: any) => string;
}
const defaultNoRowsOverlayMessage =
    'Select at least two filters from the menu or utilize the search bar to display results';

// based on getGridStringOperators() from @mui/x-data-grid-premium
const searchStringOperators = [
    { id: 'contains', display: 'Contains' },
    { id: 'equals', display: 'Equals' },
    { id: 'startsWith', display: 'Starts with' },
    { id: 'endsWith', display: 'Ends with' },
    // { id: 'isAnyOf', display: 'Is any of' },
    // { id: 'isNotAnyOf', display: 'Is not any of' },
    { id: 'isEmpty', display: 'Is empty' },
    { id: 'isNotEmpty', display: 'Is not empty' },
];

// only allow a max export of 5000 rows for now
const exportCountLimitMax = 5000;

// some of our more core grid pages are struggling with the export limit of 5000 in production. Using 100 as a temporary workaround.
// the 'rateLimitExportCount' is being applied to orders, work orders, and quotes right now.
const exportCountRateLimitMax = 500;

const defaultResultsPerPage = RESULTS_PER_PAGE;

// max count for sub queries returns
const subQueryMaxCount = 10000;

// Takes a search and converts it to a loopback filter.
const createLoopbackFilter = (search: SearchCriteria) => {
    const loopbackFilter: LoopbackFilter = {
        where: {},
    };

    const {
        field: { property },
        searchTerm: { value },
    } = search;

    switch (search.operator.id) {
        case 'contains':
            loopbackFilter.where[property] = {
                ilike: `%${value}%`,
            };
            break;
        case 'equals':
            loopbackFilter.where[property] = { ilike: value };
            break;
        case 'startsWith':
            loopbackFilter.where[property] = {
                ilike: `${value}%`,
            };
            break;
        case 'endsWith':
            loopbackFilter.where[property] = {
                ilike: `%${value}`,
            };
            break;
        case 'isEmpty':
            loopbackFilter.where = {
                or: [{ [property]: null }, { [property]: '' }],
            };
            break;
        case 'isNotEmpty':
            loopbackFilter.where = {
                and: [
                    { [property]: { neq: null } },
                    { [property]: { neq: '' } },
                ],
            };
            break;
        case 'isAnyOf':
            loopbackFilter.where[property] = {
                inq: value,
            };
            break;
        case 'isNotAnyOf':
            loopbackFilter.where[property] = {
                nin: value,
            };
            break;
        default:
            break;
    }
    return loopbackFilter;
};

const createContainsLoopbackFilter = (property: string, searchTerm: string) => {
    return {
        where: {
            [property]: {
                ilike: `%${searchTerm}%`,
            },
        },
    };
};

const mapGridHeaders = (headers: GridColDef[]): GridColDef[] => {
    const gridHeaders = headers.map(col => {
        const columnConfig: GridColDef = {
            ...col,
            ...(col.type !== 'actions' && {
                renderCell: (params: GridRenderCellParams) => {
                    if (
                        params.rowNode.type === 'group' &&
                        params.field === '__row_group_by_columns_group__'
                    ) {
                        return params.rowNode.groupingKey;
                    }
                    return params.rowNode.type !== 'group'
                        ? col.renderCell?.(params)
                        : params.value;
                },
            }),
            ...(col.minWidth !== undefined && { minWidth: col.minWidth }),
        };
        return columnConfig;
    });

    return gridHeaders;
};

const mapGridRows = <ApiData = any>(rows: ApiData[]) => {
    return rows.map(row => ({
        ...row,
        virtualId: generateRandomUUID(),
    }));
};

const processAndAddAdvancedDataGridFilters = (
    advancedFilters: { [s: string]: any } | ArrayLike<any>
) => {
    const processedAdvancedFilters = Object.values(advancedFilters).reduce(
        (acc, item) => {
            if (!isEmpty(item)) {
                if (Array.isArray(item)) {
                    acc.push(...item);
                } else {
                    acc.push(item);
                }
            }
            return acc;
        },
        []
    );

    return processedAdvancedFilters;
};

const autosizeColumns = (
    autosizeParams: {
        apiRef: MutableRefObject<GridApiPremium>;
        includeHeaders: boolean;
        includeOutliers: boolean;
        outliersFactor?: number;
        expand?: boolean;
        timeout?: number;
        columns?: GridColDef['field'][];
        disableAutoSizing?: boolean;
    },
    callback?: () => void
) => {
    if (autosizeParams.disableAutoSizing) return;
    const {
        apiRef,
        includeHeaders,
        includeOutliers,
        outliersFactor,
        expand,
        timeout,
        columns,
    } = autosizeParams;
    /* setTimeout per recommendation from MUI to avoid issues with virtualization in conjunction with autosizing
     * https://mui.com/x/react-data-grid/column-dimensions/#autosizing-asynchronously
     */

    // Fix MUI DataGrid autosizeColumns incorrect width calculation when pinned columns are present.
    // By temporarily unpinning columns before autosizeColumns, then restoring the pin state.
    //
    // In /@mui/x-data-grid/hooks/features/columnResize/useGridColumnResize.js:
    //
    // 1. `totalWidth` is calculated from `visibleColumns`, which already INCLUDES pinned columns:
    //    const totalWidth = visibleColumns.reduce(
    //      (total, column) =>
    //        total + (widthByField[column.field] ?? column.computedWidth ?? column.width),
    //      0
    //    );
    //
    // 2. `availableWidth` is derived from:
    //    const dimensions = apiRef.current.getRootDimensions();
    //    const availableWidth = dimensions.viewportInnerSize.width;
    //
    //    ⚠️ This width EXCLUDES pinned columns.
    //
    // 3. As a result, `remainingWidth` becomes incorrect:
    //    const remainingWidth = availableWidth - totalWidth;
    //
    //    ⚠️ Pinned column width is effectively subtracted TWICE:
    //       - Once because `availableWidth` already excludes it
    //       - Again because `totalWidth` includes it
    //
    // This causes incorrect autosizeColumns behavior when pinned columns are used.

    const pins = apiRef?.current?.getPinnedColumns();
    apiRef?.current?.setPinnedColumns({
        left: [],
        right: [],
    });

    setTimeout(() => {
        apiRef?.current?.autosizeColumns({
            includeHeaders,
            includeOutliers,
            outliersFactor,
            expand,
            columns,
        });

        apiRef.current.setPinnedColumns(pins);
        if (callback) callback();
    }, timeout ?? 250);
};

const handleGridExport = async <ApiData = any>(
    format: string,
    apiRef: MutableRefObject<GridApiPremium>,
    fetch: Promise<ApiData[] | any>,
    fileName?: string,
    mapper: (
        rows: ApiData[],
        ...args: any[]
    ) => GridValidRowModel[] = mapGridRows<ApiData>,
    dataProperty: string = 'workOrders'
) => {
    try {
        const allRowsFetch = await fetch;

        let dataToMap: ApiData[];
        if (Array.isArray(allRowsFetch)) {
            dataToMap = allRowsFetch;
        } else if (allRowsFetch && typeof allRowsFetch === 'object') {
            dataToMap = allRowsFetch[dataProperty] || [];
        } else {
            dataToMap = [];
        }
        const data = mapper(dataToMap);

        if (!apiRef.current.state.virtualization.enabled) {
            apiRef.current.unstable_setVirtualization(true); // Virtualize the grid to handle large data
        }
        apiRef.current.setRows(data);
        apiRef.current.selectRows([], false, true); // Deselects all rows in case user has clicked on a row to include all rows for export instead of just the selected row
        const exportOptions = { fileName };
        switch (format) {
            case 'csv':
                await apiRef.current.exportDataAsCsv(exportOptions);
                break;
            case 'excel':
                await apiRef.current.exportDataAsExcel(exportOptions);
                break;
            default:
                break;
        }
        return data;
    } catch (error: any) {
        logger.error(error);
        throw error;
    }
};

const createExceljsPostProcess = (
    ids: GridRowId[],
    apiRef: MutableRefObject<GridApiPremium>,
    customColumns: CustomColumnType[],
    exportData: any[]
) => {
    return async ({ worksheet }: GridExceljsProcessInput) => {
        const totalColumns = worksheet.columnCount;
        worksheet.spliceColumns(1, totalColumns);

        const headers = customColumns.map(c => c.headerName);
        worksheet.insertRow(1, headers);

        const customData: string[][] = [];
        ids.forEach((id: GridRowId, dataRowIndex: number) => {
            const customDataRow: string[] = map(customColumns, col => {
                return col.valueGetter
                    ? col.valueGetter(
                          null,
                          exportData[dataRowIndex],
                          col,
                          apiRef
                      )
                    : exportData[dataRowIndex][col.field];
            });

            customData.push(customDataRow);
        });
        worksheet.spliceRows(2, worksheet.rowCount, ...customData);
    };
};

const handleCustomExportCsv = (
    ids: GridRowId[],
    apiRef: MutableRefObject<GridApiPremium>,
    customColumns: CustomColumnType[],
    exportData: any[],
    exportOptions: GridFileExportOptions
) => {
    const csv = apiRef.current.getDataAsCsv({
        ...exportOptions,
        includeHeaders: true,
    });
    const lines = csv.split('\n');

    const finalHeader = customColumns
        .map(column => column.headerName)
        .join(',');

    const finalBody = lines.slice(1).map((line, idx) => {
        const dataRow = exportData[idx];

        const customRow = customColumns
            .map(column => {
                let customValue = column.valueGetter
                    ? column.valueGetter(null, dataRow, column, apiRef)
                    : dataRow[column.field];
                if (isArray(customValue) || isObject(customValue)) {
                    customValue = JSON.stringify(customValue);
                }
                customValue = toString(customValue)
                    ?.replace(/'/g, "\\'")
                    ?.replace(/"/g, '""');

                customValue = customValue || '';

                return `"${customValue}"`;
            })
            .join(',');

        return customRow;
    });

    const finalCsv = [finalHeader, ...finalBody].join('\n');
    const blob = new Blob([finalCsv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${exportOptions.fileName}.csv`;
    a.click();
    URL.revokeObjectURL(url);
};

const handleCurrentDataGridExport = async (
    format: string,
    apiRef: MutableRefObject<GridApiPremium>,
    exportData: any[],
    resetData: () => void,
    fileName: string,
    customExportColumns?: any[]
) => {
    try {
        const api = apiRef?.current;
        if (!api) return;
        api.setRows(exportData);
        const ids = Array.from(apiRef.current.getAllRowIds());
        const exportOptions: GridFileExportOptions = {
            fileName,
            getRowsToExport: () => ids,
            allColumns: true,
        };

        setTimeout(async () => {
            switch (format) {
                case 'csv':
                    if (customExportColumns) {
                        handleCustomExportCsv(
                            ids,
                            apiRef,
                            customExportColumns,
                            exportData,
                            exportOptions
                        );
                    } else {
                        await apiRef.current.exportDataAsCsv(exportOptions);
                    }
                    break;
                case 'excel':
                    if (customExportColumns) {
                        (
                            exportOptions as GridExcelExportOptions
                        ).exceljsPostProcess = createExceljsPostProcess(
                            ids,
                            apiRef,
                            customExportColumns,
                            exportData
                        );
                    }
                    await apiRef.current.exportDataAsExcel(exportOptions);
                    break;
                default:
                    break;
            }
            resetData();
        }, 0);
    } catch (error: any) {
        logger.error(error);
        throw error;
    }
};

const parseGroupField = (field: string = ''): string => {
    const prefix = '__row_group_by_columns_group_';
    const suffix = '__';
    if (field.startsWith(prefix)) {
        field = field.substring(prefix.length);
    }
    if (field.endsWith(suffix)) {
        field = field.substring(0, field.length - suffix.length);
    }
    return field;
};

const detectGridStateChanges = (
    activeId: number,
    currentGridState: Partial<DataGridStateType>,
    gridViews: DataGridStateView<DataGridStateType>[]
) => {
    if (
        activeId !== 0 &&
        gridViews?.length &&
        gridViews?.find(
            (view: DataGridStateView<DataGridStateType>) => view.id === activeId
        )
    ) {
        const selectedView = gridViews?.find(
            (view: DataGridStateView<DataGridStateType>) => view.id === activeId
        );
        return !isEqual(selectedView?.gridState, currentGridState);
    }
    return (
        activeId === 0 ||
        !gridViews?.some(
            (view: DataGridStateView<DataGridStateType>) => view.id === activeId
        )
    );
};

// common grid hooks
const useGridInitialization = (
    gridName: GRID_NAME,
    loadingSavedGridViews: boolean,
    initialGridState: Partial<DataGridState>,
    savedGridViews?: DataGridStateView<DataGridStateType>[]
) => {
    const dispatch = useDispatch();

    const hasDefaultView = useMemo(
        () => !!savedGridViews?.find(view => view.isDefaultView),
        [savedGridViews]
    );

    const initializeGrid = useCallback(
        (defaultFilters?: Partial<AdvancedFiltersState>) => {
            if (loadingSavedGridViews) return false;

            if (hasDefaultView) {
                const defaultView = savedGridViews?.find(
                    view => view.isDefaultView
                );
                dispatch(
                    loadGridState({
                        gridName,
                        initialState:
                            defaultView?.gridState as DataGridStateType,
                    })
                );
                dispatch(
                    setGridViewActiveId({
                        gridName,
                        activeId: defaultView?.id as number,
                    })
                );
            } else {
                dispatch(
                    initializeGridState({
                        gridName,
                        initialState: defaultFilters
                            ? {
                                  ...initialGridState,
                                  advancedFilters: defaultFilters,
                              }
                            : initialGridState,
                    })
                );
                if (initialGridState?.advancedFilters || defaultFilters) {
                    const _advancedFilters =
                        initialGridState?.advancedFilters ||
                        defaultFilters ||
                        {};
                    const advancedFilterCount =
                        Object.keys(_advancedFilters).length;
                    dispatch(
                        setAdvancedFilterCount({
                            gridName,
                            advancedFilterCount,
                        })
                    );
                }
            }

            // Reset to page 1 and skip 0
            dispatch(
                setActivePaginationPage({
                    gridName,
                    activePaginationPage: 1,
                })
            );

            dispatch(
                setActiveQuery({
                    gridName,
                    activeQuery: { skip: 0 },
                })
            );

            return true;
        },
        [
            dispatch,
            gridName,
            hasDefaultView,
            initialGridState,
            loadingSavedGridViews,
            savedGridViews,
        ]
    );

    return { initializeGrid, hasDefaultView };
};

const useGridQueryFunctions = (
    gridName: GRID_NAME,
    activePaginationPage: number,
    resultsPerPage: number,
    disableQueryRules: boolean,
    advancedFilterCount?: number,
    searchFilters: Record<string, DataGridSearchFilter[]> = {},
    advancedFilters: Partial<AdvancedFiltersState> = {}
) => {
    const dispatch = useDispatch();

    const canFetchQuery = useMemo(
        // CS-2509: we won't allow the user to search data unless they have
        // one search filter, two advanced filters or a default filter.
        () =>
            Object.keys(searchFilters)?.length > 0 ||
            (advancedFilterCount && advancedFilterCount > 1) ||
            disableQueryRules,
        [searchFilters, advancedFilterCount, disableQueryRules]
    );

    const handleSort = useCallback(
        (header: GridSortItem) => {
            if (!header) {
                dispatch(
                    setActivePaginationPage({
                        gridName,
                        activePaginationPage: 1,
                    })
                );

                dispatch(
                    setActiveQuery({
                        gridName,
                        activeQuery: { skip: 0 },
                    })
                );
                return;
            }

            const { sort, field } = header;

            if (field.includes('__row_group_by_columns')) {
                return;
            }

            dispatch(
                setActivePaginationPage({
                    gridName,
                    activePaginationPage: 1,
                })
            );

            dispatch(
                setActiveQuery({
                    gridName,
                    activeQuery: {
                        skip: 0,
                        order: [`${field} ${sort}`],
                    },
                })
            );
        },
        [dispatch, gridName]
    );

    const handlePagination = useCallback(
        (selectedPage: number) => {
            dispatch(
                setActivePaginationPage({
                    gridName,
                    activePaginationPage: selectedPage,
                })
            );

            dispatch(
                setActiveQuery({
                    gridName,
                    activeQuery: { skip: (selectedPage - 1) * resultsPerPage },
                })
            );
        },
        [dispatch, gridName, resultsPerPage]
    );

    const handleInfiniteScroll = useCallback(() => {
        dispatch(
            setActivePaginationPage({
                gridName,
                activePaginationPage: activePaginationPage + 1,
            })
        );

        dispatch(
            setActiveQuery({
                gridName,
                activeQuery: { skip: activePaginationPage * resultsPerPage },
            })
        );
    }, [activePaginationPage, dispatch, gridName, resultsPerPage]);

    return {
        handleSort,
        handlePagination,
        handleInfiniteScroll,
        canFetchQuery,
    };
};

const calculateAdvancedFilterCount = (
    advancedFilters: Partial<AdvancedFiltersState>
) => {
    const advancedFilterCounts: Record<string, number> = {};
    Object.entries(advancedFilters).forEach(([key, value]) => {
        let advancedFilterCount = 0;
        if (Array.isArray(value)) {
            advancedFilterCount += value.length;
        } else if (typeof value === 'object' && value !== null) {
            advancedFilterCount += 1;
        }
        advancedFilterCounts[key] = advancedFilterCount;
    });
    return advancedFilterCounts;
};

const useGridInitializationWithColumnSync = (
    gridName: GRID_NAME,
    initialGridState: Partial<DataGridState>,
    columnOrdering: string[] | undefined,
    storeInitialized: boolean
) => {
    const dispatch = useDispatch();

    useEffect(() => {
        dispatch(
            initializeGridState({
                gridName,
                initialState: initialGridState,
            })
        );
    }, [dispatch, gridName, initialGridState]);

    useEffect(() => {
        if (
            initialGridState?.columnOrdering &&
            columnOrdering &&
            storeInitialized
        ) {
            const columnsMissingInCache = initialGridState?.columnOrdering.some(
                column => columnOrdering.indexOf(column) === -1
            );
            if (columnsMissingInCache) {
                dispatch(
                    setColumnOrdering({
                        gridName,
                        columnOrdering: initialGridState?.columnOrdering,
                    })
                );
            }
        }
    }, [
        columnOrdering,
        dispatch,
        gridName,
        initialGridState?.columnOrdering,
        storeInitialized,
    ]);
};

const setMaxExportLimit = (
    resultCount: number,
    exportLimit: number = exportCountLimitMax
) => {
    if (resultCount > exportLimit) {
        return exportLimit;
    }
    return resultCount;
};

export {
    mapGridHeaders,
    mapGridRows,
    searchStringOperators,
    processAndAddAdvancedDataGridFilters,
    createLoopbackFilter,
    createContainsLoopbackFilter,
    autosizeColumns,
    handleGridExport,
    handleCurrentDataGridExport,
    parseGroupField,
    exportCountLimitMax,
    exportCountRateLimitMax,
    subQueryMaxCount,
    defaultResultsPerPage,
    detectGridStateChanges,
    useGridInitialization,
    useGridInitializationWithColumnSync,
    useGridQueryFunctions,
    defaultNoRowsOverlayMessage,
    calculateAdvancedFilterCount,
    setMaxExportLimit,
};
